import { Chip } from '@/components/Chip/Chip';
import { Icon } from '@/components/Icon/Icon';
import { IconButton } from '@/components/IconButton/IconButton';
import type { LenderDocument } from '@/graphql/lenders';
import { formatBytes, formatDay } from '@/lib/format';

import styles from './LenderDocumentList.module.css';

export interface LenderDocumentListProps {
  /** In the order clients are given them. */
  documents: readonly LenderDocument[];
  /** Draws the move controls. */
  canEdit: boolean;
  /** A reorder is in flight; every move control waits for it. */
  reordering?: boolean;
  downloadingId?: string | null;
  onOpen: (document: LenderDocument) => void;
  onMove: (document: LenderDocument, step: -1 | 1) => void;
  onDownload: (document: LenderDocument) => void;
}

/** A lender's documents, each opening its details and history. */
export function LenderDocumentList({
  documents,
  canEdit,
  reordering = false,
  downloadingId = null,
  onOpen,
  onMove,
  onDownload,
}: LenderDocumentListProps) {
  return (
    <ol aria-label="Documents" className={styles.list}>
      {documents.map((document, index) => {
        const version = document.currentVersion;
        const title = version?.title ?? document.key ?? 'Untitled document';
        const archived = document.status === 'ARCHIVED';
        return (
          <li className={`${styles.row} ${archived ? styles.archived : ''}`} key={document.id}>
            {canEdit ? (
              <span className={styles.move}>
                <IconButton
                  disabled={reordering || index === 0}
                  icon="caret-up"
                  label={`Move ${title} up`}
                  onClick={() => onMove(document, -1)}
                />
                <IconButton
                  disabled={reordering || index === documents.length - 1}
                  icon="caret-down"
                  label={`Move ${title} down`}
                  onClick={() => onMove(document, 1)}
                />
              </span>
            ) : (
              <span aria-hidden="true" className={styles.position}>
                {index + 1}
              </span>
            )}
            <button className={styles.open} onClick={() => onOpen(document)} type="button">
              <span className={styles.icon}>
                <Icon name="file-pdf" size="1.35rem" />
              </span>
              <span className={styles.main}>
                <span className={styles.title}>{title}</span>
                <span className={styles.meta}>
                  {[
                    version?.versionNo ? `Version ${version.versionNo}` : null,
                    version?.file?.filename ?? null,
                    version?.file?.sizeBytes != null ? formatBytes(version.file.sizeBytes) : null,
                    `Updated ${formatDay(version?.createdAt ?? document.updatedAt)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </button>
            {archived ? <Chip tone="warn">Archived</Chip> : <Chip tone="good">Active</Chip>}
            <span className={styles.actions}>
              <IconButton
                busy={downloadingId === document.id}
                disabled={!version?.fileId}
                icon="download-simple"
                label={`Download ${title}`}
                onClick={() => onDownload(document)}
              />
              <IconButton
                icon="clock-counter-clockwise"
                label={`History of ${title}`}
                onClick={() => onOpen(document)}
              />
            </span>
          </li>
        );
      })}
    </ol>
  );
}
